import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import TaskAssigneePicker from '../components/TaskAssigneePicker';
import { useAuth } from '../context/AuthContext';
import {
  createElevatorAppointment,
  elevatorSurveyPhotoUrl,
  fetchElevatorInitialSurvey,
  type ElevatorInitialSurveyRow,
  type ElevatorSurveyPhoto,
} from '../api/elevatorAppointments';
import { fetchVerifiedUsers, type VerifiedUser } from '../api/users';
import { AttachmentVoidPill, AttachmentVoidReason } from '../components/AttachmentVoidNote';
import { splitVoided } from '../api/attachmentVoid';
import { ELEVATOR_SURVEY_FORM, elevatorSurveyPhotoLabel } from '../data/elevatorSurveyForm';
import './Page.css';
import './Events.css';
import './Elevators.css';

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fieldValue(form: Record<string, unknown>, id: string): string {
  const v = form[id];
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

const ElevatorInitialSurveyDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const idNum = id ? parseInt(id, 10) : NaN;

  const [survey, setSurvey] = useState<ElevatorInitialSurveyRow | null>(null);
  const [photos, setPhotos] = useState<ElevatorSurveyPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Voided photos are kept, but listed apart from the ones that count as the survey.
  const { active: livePhotos, voided: voidedPhotos } = useMemo(() => splitVoided(photos), [photos]);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [appointmentAt, setAppointmentAt] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  const load = useCallback(async () => {
    if (!Number.isInteger(idNum) || idNum < 1) {
      setError('Invalid survey.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchElevatorInitialSurvey(idNum);
      setSurvey(data);
      setPhotos(data.photos ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load survey.');
      setSurvey(null);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [idNum]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchVerifiedUsers()
      .then((list) => !cancelled && setVerifiedUsers(list))
      .catch(() => !cancelled && setVerifiedUsers([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const assigneePickerUsers = useMemo(() => {
    const withElevator = (u: VerifiedUser) => (u.roles ?? []).includes('elevator');
    return [...verifiedUsers]
      .sort((a, b) => {
        const ae = withElevator(a) ? 0 : 1;
        const be = withElevator(b) ? 0 : 1;
        if (ae !== be) return ae - be;
        return a.username.localeCompare(b.username);
      })
      .map((u) => ({ id: u.id, username: u.username }));
  }, [verifiedUsers]);

  const submitSchedule = async () => {
    if (!user?.id || !survey) return;
    if (!appointmentAt) {
      setScheduleError('Appointment date and time is required.');
      return;
    }
    const form = survey.form_data || {};
    const businessName = fieldValue(form, 'customer_property_name') || survey.business_name || 'Elevator appointment';
    setSubmitting(true);
    setScheduleError('');
    try {
      const created = await createElevatorAppointment({
        user_id: user.id,
        client_id: survey.client_id,
        business_name: businessName,
        business_address: fieldValue(form, 'property_address') || null,
        point_of_contact: fieldValue(form, 'service_contact_name') || fieldValue(form, 'surveyed_by') || null,
        poc_phone: fieldValue(form, 'service_phone') || null,
        poc_email: fieldValue(form, 'service_email') || null,
        appointment_at: new Date(appointmentAt).toISOString(),
        assignee_user_ids: assigneeIds,
        survey_id: survey.id,
      });
      navigate(`/elevators/${created.id}`);
    } catch (e) {
      setScheduleError(e instanceof Error ? e.message : 'Failed to schedule appointment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <PageHeader title="Initial survey" />
            <div className="page-content">
              <p className="page-subtitle">Loading…</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <PageHeader title="Initial survey" />
            <div className="page-content">
              <p className="create-order-error" role="alert">
                {error || 'Survey not found.'}
              </p>
              <button type="button" className="page-toolbar-btn" onClick={() => navigate('/elevators?tab=surveys')}>
                ← Back to Elevators
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const form = survey.form_data || {};
  const alreadyScheduled = survey.elevator_appointment_id != null;

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader title="Elevator initial survey" subtitle="Client-submitted survey" />
          <div className="page-content">
            <button type="button" className="elevator-back-link" onClick={() => navigate('/elevators?tab=surveys')}>
              ← Back to Elevators
            </button>

            <div className="elevator-detail-title-row">
              <h1 className="project-detail-title" style={{ margin: 0 }}>
                {fieldValue(form, 'customer_property_name') || survey.business_name || 'Elevator survey'}
                <span
                  className={`elevator-status-badge ${
                    survey.status === 'Scheduled' ? 'elevator-status-badge--completed' : 'elevator-status-badge--scheduled'
                  }`}
                >
                  {survey.status}
                </span>
              </h1>
              <div className="elevator-detail-actions">
                {alreadyScheduled ? (
                  <button
                    type="button"
                    className="elevator-btn elevator-btn--reopen"
                    onClick={() => navigate(`/elevators/${survey.elevator_appointment_id}`)}
                  >
                    View appointment
                  </button>
                ) : (
                  <button
                    type="button"
                    className="elevator-btn elevator-btn--complete"
                    disabled={!survey.contract_signed}
                    title={survey.contract_signed ? undefined : 'The contract must be signed before scheduling an appointment.'}
                    onClick={() => {
                      setAppointmentAt('');
                      setAssigneeIds([]);
                      setScheduleError('');
                      setScheduleOpen(true);
                    }}
                  >
                    Schedule elevator appointment
                  </button>
                )}
              </div>
            </div>

            <p className="page-subtitle" style={{ marginTop: '-0.25rem' }}>
              Submitted {fmt(survey.submitted_at)}
              {survey.order_number ? ` · Order ${survey.order_number}` : ''}
            </p>

            <div
              className={`survey-contract-status survey-contract-status--${
                survey.contract_signed ? 'signed' : survey.has_contract ? 'unsigned' : 'none'
              }`}
              role="status"
            >
              {survey.contract_signed ? (
                <>Contract signed — you can schedule the elevator appointment.</>
              ) : survey.has_contract ? (
                <>Contract sent but not signed yet. The appointment can be scheduled once the client signs the contract.</>
              ) : (
                <>No contract has been sent for this order yet. A signed contract is required before scheduling.</>
              )}
            </div>

            {ELEVATOR_SURVEY_FORM.map((section) => (
              <section className="elevator-info-card" key={section.title} style={{ marginBottom: '1rem' }}>
                <h2 className="elevator-form-section-title" style={{ marginTop: 0 }}>
                  {section.title}
                </h2>
                <div className="elevator-info-grid">
                  {section.fields.map((f) => (
                    <div className="elevator-info-item" key={f.id}>
                      <span className="elevator-info-label">{f.label}</span>
                      <span className="elevator-info-value">{fieldValue(form, f.id) || '—'}</span>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            <section className="elevator-info-card" style={{ marginBottom: '1rem' }}>
              <h2 className="elevator-form-section-title" style={{ marginTop: 0 }}>
                Elevator Service Provider — Photos
              </h2>
              {livePhotos.length === 0 ? (
                <p className="page-subtitle" style={{ margin: 0 }}>
                  The client did not upload any photos with this survey.
                </p>
              ) : (
                <div className="survey-photo-grid">
                  {livePhotos.map((photo) => (
                    <a
                      key={photo.id}
                      className="survey-photo-tile"
                      href={elevatorSurveyPhotoUrl(survey.id, photo.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={elevatorSurveyPhotoUrl(survey.id, photo.id)} alt={elevatorSurveyPhotoLabel(photo.field_id)} />
                      <span>{elevatorSurveyPhotoLabel(photo.field_id)}</span>
                    </a>
                  ))}
                </div>
              )}

              {voidedPhotos.length > 0 ? (
                <div className="attachment-voided-group">
                  <p className="attachment-voided-group-title">Voided photos</p>
                  <div className="survey-photo-grid">
                    {voidedPhotos.map((photo) => (
                      <div key={photo.id} className="attachment-voided-thumb">
                        <a
                          className="survey-photo-tile"
                          href={elevatorSurveyPhotoUrl(survey.id, photo.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img src={elevatorSurveyPhotoUrl(survey.id, photo.id)} alt={elevatorSurveyPhotoLabel(photo.field_id)} />
                          <span>
                            {elevatorSurveyPhotoLabel(photo.field_id)} <AttachmentVoidPill />
                          </span>
                        </a>
                        <AttachmentVoidReason attachment={photo} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          </div>
        </main>
      </div>

      <Modal isOpen={scheduleOpen} onClose={() => setScheduleOpen(false)} title="Schedule elevator appointment" wide>
        <div className="modal-body">
          <p className="page-subtitle" style={{ marginTop: 0 }}>
            An email with the date/time and a Schedule Conflict link will be sent to the client's email on the survey.
          </p>
          <div className="form-group">
            <label htmlFor="survey-appt-at" className="form-label">
              Appointment Date and Time
            </label>
            <input
              id="survey-appt-at"
              type="datetime-local"
              className="form-input"
              value={appointmentAt}
              onChange={(e) => {
                setAppointmentAt(e.target.value);
                setScheduleError('');
              }}
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <span className="form-label">Assigned employees</span>
            <TaskAssigneePicker
              users={assigneePickerUsers}
              value={assigneeIds}
              onChange={setAssigneeIds}
              addPlaceholder="Search employee to assign…"
            />
          </div>
          {scheduleError ? (
            <p className="create-order-error" role="alert">
              {scheduleError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={submitting} onClick={() => setScheduleOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={submitting} onClick={() => void submitSchedule()}>
            {submitting ? 'Scheduling…' : 'Schedule & notify client'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ElevatorInitialSurveyDetail;
